import React, { CSSProperties, useEffect, useState } from 'react';
import styles from './CoverageCheckSummaryView.module.scss';
import {
  generateCSV,
  IExtractionResult,
  resetFieldsValues,
  setFieldsValues,
  transformText,
  capitalize,
  transformExtractionResult
} from 'app/utils';
import {
  getBootstrapApp,
  getBusinessArea,
  getCoverageCheckV2Files,
  getCoverageCheckV2Id,
  getService,
  setCoverageCheckV2Files,
  setCurrentSummary,
  setGeneratingExtraction,
  setRegenerateExtraction,
  useAppSelector
} from 'app/redux';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  ActionButtons,
  Chip,
  DownloadCSVButton,
  GeneratingExtraction,
  DisplaySection,
  SummaryField
} from 'app/components';
import { getCoverageCheckV2 } from 'integration';
import config from 'config';
import { SERVICE_NAMES, UNDEFINED_FILES } from 'app/utils/consts';

export const CoverageCheckV2SummaryView: React.FC = () => {
  const AVG_DURATION_IN_MS = 110000;

  const currentService = useAppSelector(getService);
  const currentBusinessArea = useAppSelector(getBusinessArea) ?? 'claims';
  const bootstrap = useAppSelector(getBootstrapApp);
  const claimId = useAppSelector(getCoverageCheckV2Id);
  const businessAreas = bootstrap.businessAreas;
  const services = bootstrap.services;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentFiles = useAppSelector(getCoverageCheckV2Files);

  const [claimsFields, setClaimsFields] = useState<
    Array<{ query: string; value?: string }>
  >([]);
  const [policyFields, setPolicyFields] = useState<
    Array<{ query: string; value?: string }>
  >([]);
  const [costFields, setCostFields] = useState<
    Array<{ query: string; value?: string }>
  >([]);
  const [termsAndConditionFields, setTermsAndConditionFields] = useState<
    Array<{ query: string; value?: string }>
  >([]);
  const [coverageVeredict, setCoverageVeredict] = useState<
    Array<{ query: string; value?: string }>
  >([]);

  const [originalExtraction, setOriginalExtraction] =
    useState<IExtractionResult>();
  const [policyId, setPolicyId] = useState<
    | {
        query: string;
        value?: string;
      }
    | undefined
  >(undefined);

  const checkFiles = (currentFiles: Record<string, any>): boolean => {
    const { claims, policy, terms } = currentFiles;
    return (
      claims instanceof File && policy instanceof File && terms instanceof File
    );
  };

  const fetchCoverageCheckCall = async (files: any): Promise<void> => {
    dispatch(setGeneratingExtraction(true));
    dispatch(setRegenerateExtraction(true));
    const { claims, policy, terms, extra } = files;

    const body = new FormData();
    body.append('claimsFile', claims, claims.name);
    body.append('policyFile', policy, policy.name);
    body.append('termsAndCondsFile', terms, terms.name);
    extra && body.append('extraFile', extra, extra.name);

    setCurrentSummary(undefined);
    await getCoverageCheckV2(
      String(currentBusinessArea),
      'coverage-check',
      body
    )
      .then((response) => {
        onClaimsNotificationResponse(response?.claimsNotification);
        onPolicyResponse(response?.policyContract);
        onCostsResponse(response?.extraFile);
        onTermsAndConditionResponse(response?.termsAndConditions);
        onCoverageResponse(response?.coverage);
      })
      .catch(() => {
        resetFieldsValues();
        navigate(config.APP.PATH.COVERAGE_CHECK_V2);
      })
      .finally(() => {
        dispatch(setGeneratingExtraction(false));
      });
  };

  const onClaimsNotificationResponse = (response: any): void => {
    const currentSummary: IExtractionResult = {};
    const responseFields = [];

    for (const field in response) {
      const value = response[field];
      const formattedField = transformText(field);
      if (field === 'policy_id') {
        setPolicyId({ query: formattedField, value });
      } else {
        currentSummary[formattedField] = { value: value ?? 'N/A' };

        responseFields.push({ query: formattedField, value: value ?? 'N/A' });
      }
    }

    setOriginalExtraction(currentSummary);
    setClaimsFields(responseFields);
    setFieldsValues(currentSummary);
  };

  const onPolicyResponse = (response: any): void => {
    const currentSummary: IExtractionResult = {};
    const responseFields = [];

    for (const field in response) {
      const value = response[field] ?? 'N/A';
      const formattedField = transformText(field);
      currentSummary[formattedField] = { value };
      responseFields.push({ query: formattedField, value });
    }
    setOriginalExtraction(currentSummary);
    setPolicyFields(responseFields);
    setFieldsValues(currentSummary);
  };

  const onCostsResponse = (response: any): void => {
    const currentSummary: IExtractionResult = {};
    const responseFields = [];

    for (const field in response) {
      const value = response[field] ?? 'N/A';
      const formattedField = transformText(field);
      if (Array.isArray(value)) {
        const joinedValue = capitalize(value.join('\n')) ?? value.join('\n');
        currentSummary[formattedField] = { value: joinedValue ?? 'N/A' };
        responseFields.push({
          query: formattedField,
          value: joinedValue ?? 'N/A'
        });
      } else {
        currentSummary[formattedField] = { value };
        responseFields.push({ query: formattedField, value });
      }
    }
    setOriginalExtraction(currentSummary);
    setCostFields(responseFields);
    setFieldsValues(currentSummary);
  };

  const onTermsAndConditionResponse = (response: any): void => {
    const currentSummary: IExtractionResult = {};
    const responseFields = [];

    for (const field in response) {
      const value = response[field] ?? 'N/A';
      const formattedField = transformText(field);

      if (Array.isArray(value)) {
        const joinedValue = value.join('\n\n');
        currentSummary[formattedField] = { value: joinedValue ?? 'N/A' };
        responseFields.push({
          query: formattedField,
          value: joinedValue ?? 'N/A'
        });
      } else {
        currentSummary[formattedField] = { value };
        responseFields.push({ query: formattedField, value });
      }
    }
    setOriginalExtraction(currentSummary);
    setTermsAndConditionFields(responseFields);
    setFieldsValues(currentSummary);
  };

  const onCoverageResponse = (response: any): void => {
    const currentSummary: IExtractionResult = {};
    const responseFields = [];

    for (const field in response) {
      const value = response[field];
      const formattedField = transformText(field);
      currentSummary[formattedField] = { value };
      responseFields.push({ query: formattedField, value });
    }
    setOriginalExtraction(currentSummary);
    setCoverageVeredict(responseFields);
    setFieldsValues(currentSummary);
  };

  const combineFieldsForCSV = (): IExtractionResult => {
    const combinedFields: IExtractionResult = {};

    const addFields = (
      fields: Array<{ query: string; value?: string }>
    ): void => {
      fields.forEach((field) => {
        if (field.query) {
          let sanitizedValue = field.value ?? 'N/A';

          sanitizedValue = sanitizedValue
            .replace(/;/g, ' ')
            .replace(/ - /g, ': ')
            .replace(/(\r\n|\n|\r)/gm, ', ')
            .replace(/#/g, ' ');

          combinedFields[field.query] = {
            value: sanitizedValue
          };
        }
      });
    };

    addFields(claimsFields);
    addFields(policyFields);
    addFields(costFields);
    addFields(termsAndConditionFields);
    addFields(coverageVeredict);

    return combinedFields;
  };

  const getCustomStyle = (value?: string): CSSProperties | undefined => {
    if (!value) return undefined;

    if (value.startsWith('Covered')) {
      return {
        backgroundColor: 'rgb(20, 120, 46, 0.2)',
        borderColor: 'var(--color-primary-data-positive)'
      };
    } else if (value.startsWith('Not Covered')) {
      return {
        backgroundColor: 'rgb(191, 50, 38, 0.2)',
        borderColor: 'var(--color-primary-data-negative)'
      };
    } else if (value.startsWith('Inconclusive')) {
      return {
        backgroundColor: 'rgb(255, 200, 40, 0.2)',
        borderColor: 'var(--color-primary-data-warning)'
      };
    } else {
      return undefined;
    }
  };

  const handleEraseDocument = (): void => {
    dispatch(setCoverageCheckV2Files(UNDEFINED_FILES));
  };

  const renderCoverageCheckTitle = (): React.JSX.Element => {
    return (
      <div className={styles['coverage-check-v2-title']}>
        <span className={styles['coverage-check-v2-blue-text']}>
          Coverage Check
        </span>
        <span className={styles['coverage-check-v2-dark-text']}> Tool</span>
      </div>
    );
  };

  useEffect(() => {
    if (
      currentService === SERVICE_NAMES.COVERAGE_CHECK_V2 &&
      checkFiles(currentFiles)
    ) {
      fetchCoverageCheckCall(currentFiles);
    }
    if (!checkFiles) resetFieldsValues();
  }, [currentFiles]);

  useEffect(() => {
    if (originalExtraction) {
      setFieldsValues(originalExtraction);
    } else {
      resetFieldsValues();
    }
  }, [claimsFields, policyFields]);

  return (
    <div className={styles['coverage-check-v2-container']}>
      <div>
        {renderCoverageCheckTitle()}
        <h2>Selected areas</h2>

        <div className={styles.areas}>
          <div className={styles.chips}>
            {currentBusinessArea !== undefined && (
              <Chip
                value={businessAreas[currentBusinessArea].name}
                id={currentBusinessArea}
                icon="Arrows"
                selected
                className={styles['chip-area']}
              />
            )}
            {currentService !== undefined && (
              <Chip
                value={services[currentService].name}
                id={currentService}
                icon="Arrows"
                selected
                className={styles['chip-service']}
              />
            )}
          </div>
          <ActionButtons
            handleDelete={handleEraseDocument}
            showUploadNewFile={true}
            showDeleteFile={false}
            handleCustomUploadNewFile={(): void => {
              dispatch(setCoverageCheckV2Files(UNDEFINED_FILES));
              navigate(config.APP.PATH.COVERAGE_CHECK_V2);
            }}
          />
        </div>
        <div className={styles.overview}>
          <div className={styles.fields}>
            <h1 style={{ paddingTop: '0' }}>Coverage Check Overview</h1>

            {coverageVeredict.map((coverageField) => (
              <SummaryField
                key={`field-${coverageField.query}`}
                id={`field-${coverageField.query}`}
                title={coverageField.query}
                value={coverageField.value}
                fieldStyle={getCustomStyle(coverageField.value)}
              />
            ))}
          </div>

          <div className={styles.fields}>
            <h1 style={{ paddingTop: '0' }}>Identifier</h1>

            {claimId && (
              <SummaryField
                key={'field-Claim ID'}
                id={'field-Claim ID'}
                title="Claim ID"
                value={claimId}
              />
            )}

            {policyId?.query && policyId.value && (
              <SummaryField
                key={`field-${policyId.query}`}
                id={`field-${policyId.query}`}
                title={policyId?.query}
                value={policyId?.value}
              />
            )}
          </div>
        </div>

        <DisplaySection
          title="Claim Notification"
          fieldsArray={claimsFields}
          file={currentFiles.claims}
          pdfCustomStyle={{ paddingTop: '0' }}
        />

        <DisplaySection
          title="Policy Contract"
          fieldsArray={policyFields}
          file={currentFiles.policy}
        />

        <DisplaySection
          title="Terms & Conditions"
          fieldsArray={termsAndConditionFields}
          file={currentFiles.terms}
        />

        {currentFiles.extra && (
          <DisplaySection
            title="Extra Files"
            fieldsArray={costFields}
            file={currentFiles.extra}
          />
        )}
      </div>

      <DownloadCSVButton
        handleOnClick={() => {
          generateCSV(
            transformExtractionResult(combineFieldsForCSV()),
            `claimID_${claimId?.toString() ?? 'N/A'}`,
            true
          );
        }}
        isDisabled={originalExtraction === undefined}
      />

      <GeneratingExtraction processTime={AVG_DURATION_IN_MS} />
    </div>
  );
};
